import { GroupType } from '@bp/bp-graphql-types';
import { graphql } from 'client/gql';
import { useCoursesTable } from 'components/Courses/hooks/useCoursesTable';
import { CourseEditorsModal } from 'components/Courses/modals/CourseEditorsModal';
import { CourseModal } from 'components/Courses/modals/CourseModal';
import { CourseSettingsModal } from 'components/Courses/modals/CourseSettingsModal';
import { CourseViewersModal } from 'components/Courses/modals/CourseViewersModal';
import { InstitutionCoursesTable } from 'components/Courses/tables/InstitutionCoursesTable';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { updateClaims } from '../../../utils/authStore';
import styles from './InstitutionSubpages.module.scss';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromInstitutionCourses($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const InstitutionCoursesSubpageQueryDocument = graphql(`
  query InstitutionCoursesSubpageQueryData($courseWhere: GroupWhere!, $organizationUuid: ID!, $courseUuid: ID!) {
    ...UseCoursesTableFields
    ...CourseEditorsFormFields
    ...CourseViewersFormFields
  }
`);

export const InstitutionCoursesSubpage = () => {
  const { pimAuthClaims } = useAuthClaims();

  const { t } = useTranslation();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const navigate = useNavigate();

  const { ConfirmationDialog, confirmDelete } = useCoursesTable();

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);

  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [courseModalOpen, setCourseModalOpen] = useState<boolean>(false);
  const [editorsModalOpen, setEditorsModalOpen] = useState<boolean>(false);
  const [viewersModalOpen, setViewersModalOpen] = useState<boolean>(false);
  const [courseUuid, setCourseUuid] = useState<string | null>(null);
  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);

  const [, deleteCourse] = useMutation(DeleteGroupMutationDocument);

  const [{ data }] = useQuery({
    query: InstitutionCoursesSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
      courseUuid: courseUuid ?? '',
      courseWhere: {
        type: GroupType.Course,
        organization: { uuid: organizationUuid },
        schoolYear: { uuid: selectedYearUuid },
      },
    },
  });

  const permissions = usePermissionChecker();

  function handleSettings(courseUuid: string) {
    setSettingsModalOpen(true);
    setCourseUuid(courseUuid);
  }

  function handleEdit(uuid: string) {
    setCourseModalOpen(true);
    setCourseUuid(uuid);
  }

  function handleYearSelect(uuid: string) {
    setSelectedYearUuid(uuid);
  }

  function handleAdd() {
    setCourseUuid(null);
    setCourseModalOpen(true);
  }

  async function handleDelete(uuid: string, name: string) {
    const result = await confirmDelete({
      title: t('courses.delete.title'),
      message: t('courses.delete.message', { name }),
      confirmText: t('courses.delete.confirmText'),
    });
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteCourse({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  function handleNavigate(uuid: string) {
    navigate('/courses/' + uuid);
  }

  async function handleModalClose(withClaims: boolean) {
    setCourseModalOpen(false);
    setSettingsModalOpen(false);
    setEditorsModalOpen(false);
    setViewersModalOpen(false);
    setCourseUuid(null);
    if (withClaims) {
      await updateClaims(() => setClaimsTrigger(!claimsTrigger));
    }
  }

  return (
    <BpSubpage
      className={styles.courses}
      isForbidden={!permissions?.canListInstitutionCourses({ uuid: organizationUuid })}
    >
      <InstitutionCoursesTable
        loading={tableLoading}
        query={data}
        yearUuid={selectedYearUuid}
        onYearSelect={handleYearSelect}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onSettings={handleSettings}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        permissions={permissions}
      />

      <CourseModal
        isOpen={courseModalOpen}
        courseUuid={courseUuid}
        schoolYearUuid={selectedYearUuid}
        page='institution'
        onClose={() => handleModalClose(false)}
      />

      {courseUuid && (
        <CourseEditorsModal
          isOpen={editorsModalOpen}
          courseUuid={courseUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleModalClose(true)}
          query={data}
        />
      )}

      {courseUuid && (
        <CourseViewersModal
          isOpen={viewersModalOpen}
          courseUuid={courseUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleModalClose(true)}
          query={data}
        />
      )}

      {courseUuid && (
        <CourseSettingsModal
          isOpen={settingsModalOpen}
          courseUuid={courseUuid}
          onClose={() => handleModalClose(false)}
        />
      )}

      <ConfirmationDialog />
    </BpSubpage>
  );
};
